import React, { useState, Fragment, useEffect, useContext } from 'react'
import 'react-dates/initialize'
import 'react-dates/lib/css/_datepicker.css'
import { DayPickerRangeController, DayPickerSingleDateController } from 'react-dates'
import { Listbox, Menu, Transition } from '@headlessui/react'
import { CalendarIcon, CheckIcon, ChevronDownIcon } from '@heroicons/react/24/solid'
import { displayDates } from '../../../utils'
import classNames from '../../../utils/classNames'
import { AdminContext } from '../../../contexts/AdminContext'
import { getReportIntervalOptions, IReportFilterOption } from '../../../constants/reportIntervalOptions'
import { DateTime } from 'luxon'
import luxonDisplayDates from '../../../utils/luxonDisplayDates'
import moment, { Moment } from 'moment'
import parseDateTime from '~/utils/parseDateTime'

interface ComponentProps {
  visible: boolean
  startDate: DateTime
  endDate: DateTime | null
  setStartDate: (date: DateTime, hour: number) => void
  setEndDate: (date: DateTime, hour?: number) => void
  isMonthlyIntervals?: boolean
  options?: IReportFilterOption[]
  isSinglePicker?: boolean
  isMobileDrawer?: boolean
}

const ReportsDatePicker = (props: ComponentProps) => {
  const { camp } = useContext(AdminContext)

  const currentMonthOption = getReportIntervalOptions(camp, DateTime.now()).intervalOptionsMonthly.find(option => option.label === DateTime.now().toFormat('MMMM'))

  const [selectedInterval, setSelectedInterval] = useState<IReportFilterOption>(
    props.options?.length
      ? props.options.find((option) => option.start_date?.hasSame(DateTime.now(), 'month'))
      : props.isMonthlyIntervals
      ? currentMonthOption
      : getReportIntervalOptions(camp, DateTime.now()).intervalOptions[2]
  )
  const [focusedInput, setFocusedInput] = useState<'startDate' | 'endDate'>('startDate')
  const onChange = (dates: { startDate: moment.Moment; endDate: moment.Moment }) => {
    const startDateInUTC = moment.tz(dates.startDate?.format("YYYY-MM-DDTHH:mm:ss"), "utc")
    const endDateInUTC = dates.endDate && moment.tz(dates.endDate?.format("YYYY-MM-DDTHH:mm:ss"), "utc")
    
    const start: DateTime = DateTime.fromISO(startDateInUTC?.toISOString(), { zone: "utc" })?.set({ hour: 12 })
    const end: DateTime = (endDateInUTC) ? DateTime.fromJSDate(endDateInUTC?.toDate(), { zone: "utc" })?.set({ hour: 12 }) : null

    updateDates(start, end)
  }

  const updateDates = (start: DateTime, end: DateTime) => {
    const isSame = end && start.hasSame(end, 'day')

    props.setStartDate(start, isSame ? 0 : 12)
    props.setEndDate(end?.isValid ? end : null, isSame ? 23 : 12)
  }

  useEffect(() => {
    const matchingOption = (props.options?.length ? props.options : props.isMonthlyIntervals ? getReportIntervalOptions(camp, DateTime.now()).intervalOptionsMonthly : getReportIntervalOptions(camp, DateTime.now()).intervalOptions)
      .find(
      (option) =>
        option.start_date &&
        option.end_date &&
        props.startDate &&
        props.endDate &&
        option.start_date?.hasSame(props.startDate, "day") &&
        option.end_date?.hasSame(props.endDate, "day"),
    )

    if (matchingOption) {
      setSelectedInterval(matchingOption)
    } else if (props.startDate && props.endDate) {
      setSelectedInterval({
        label: 'Custom',
        start_date: props.startDate,
        end_date: props.endDate
      })
    }
  }, [props.startDate, props.endDate])

  useEffect(() => {
    if (!props.startDate || !props.endDate) {
      if (props?.options?.length) {
        const interval = props.options.find((option) => option.start_date?.hasSame(DateTime.now(), 'month'))
        setSelectedInterval(interval)
        updateDates(interval.start_date, interval.end_date)
      } else if (props.isMonthlyIntervals) {
        setSelectedInterval(currentMonthOption)
        updateDates(currentMonthOption.start_date, currentMonthOption.end_date)
      } else {
        setSelectedInterval(getReportIntervalOptions(camp, DateTime.now()).intervalOptions[2])
        updateDates(getReportIntervalOptions(camp, DateTime.now()).intervalOptions[2].start_date, getReportIntervalOptions(camp, DateTime.now()).intervalOptions[2].end_date)
      }
    }
  }, [props.isMonthlyIntervals, props.options])

  const onChangeSelectedInterval = (interval: IReportFilterOption) => {
    if (interval.label !== 'Custom') {
      updateDates(interval.start_date, interval.end_date)
    }
  }

  const onFocusChange = (input) => {
    if (input) {
      if (input === 'endDate') {
        props.setEndDate(null)
      }
      setFocusedInput(input)
    } else {
      setFocusedInput('startDate')
    }
  }

  useEffect(() => {
    if(!props.isSinglePicker) {
      setFocusedInput('startDate')
    }
  }, [props.isSinglePicker])

  return (
    <div className='flex'>
      <Listbox
        value={selectedInterval}
        onChange={(option) => {
          onChangeSelectedInterval(option)
          setSelectedInterval(option)
        }}
      >
        {({ open }) => (
          <>
            <div className='relative'>
              <Listbox.Button
                className={classNames(
                  props.isMonthlyIntervals ? 'rounded-md' : 'rounded-l-md',
                  'inline-flex justify-center items-center w-full border border-gray-300 shadow-sm px-4 py-2.5 bg-white font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-gray-100 open-sans noOutline'
                )}
              >
                {selectedInterval.label}
                <span className=''>
                  <ChevronDownIcon className='ml-2 mt-0.5 h-5.5 w-5.5' aria-hidden='true' />
                </span>
              </Listbox.Button>

              <Transition show={open} as={Fragment} leave='transition ease-in duration-100' leaveFrom='opacity-100' leaveTo='opacity-0'>
                <Listbox.Options
                  className={classNames(
                    props.isMobileDrawer
                      ? 'fixed left-0 right-0 bottom-[calc(100%+0.5rem)] z-[60] mx-4'
                      : 'absolute z-10 mt-1 w-56',
                      'bg-white shadow-lg max-h-96 rounded-md py-1 text-base overflow-auto focus:outline-none'
                  )}
                >
                  {(props.options?.length ? props.options : props.isMonthlyIntervals ? getReportIntervalOptions(camp, DateTime.now()).intervalOptionsMonthly : getReportIntervalOptions(camp, DateTime.now()).intervalOptions).map(
                    (option, idx) => (
                      <Listbox.Option
                        key={idx}
                        className={({ active }) =>
                          classNames(active && 'bg-gray-100', 'cursor-pointer select-none relative py-2 pl-3 pr-9')
                        }
                        value={option}
                      >
                        {({ selected, active }) => (
                          <>
                            <span className={classNames(selected ? 'font-medium text-green-600' : 'font-normal', 'block truncate')}>
                              {option.label}
                            </span>

                            {selected ? (
                              <span className={classNames('text-green-600 absolute inset-y-0 right-0 flex items-center pr-4')}>
                                <CheckIcon className='h-5 w-5' aria-hidden='true' />
                              </span>
                            ) : null}
                          </>
                        )}
                      </Listbox.Option>
                    )
                  )}
                </Listbox.Options>
              </Transition>
            </div>
          </>
        )}
      </Listbox>
      <Menu as='div' className='relative inline-block text-left z-10 mr-auto'>
        <div>
          <Menu.Button
            className={classNames(
              props.isMonthlyIntervals ? 'hidden' : 'inline-flex',
              'justify-center items-center w-full border border-l-0 rounded-r-md border-gray-300 shadow-sm px-4 py-2.5 bg-white font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2  focus:ring-offset-gray-100 open-sans noOutline'
            )}
          >
            <CalendarIcon className='mr-3.5 -ml-1 mb-0.25 mt-0.25 h-5.5 w-5.5' aria-hidden='true' />
            {luxonDisplayDates(props.startDate, props.endDate && props.startDate.hasSame(props.endDate, 'day') ? null : props.endDate)}
          </Menu.Button>
        </div>

        <Transition
          as={Fragment}
          enter='transition ease-out duration-100'
          enterFrom='transform opacity-0 scale-95'
          enterTo='transform opacity-100 scale-100'
          leave='transition ease-in duration-75'
          leaveFrom='transform opacity-100 scale-100'
          leaveTo='transform opacity-0 scale-95'
        >
          <Menu.Items
            className={classNames(
              props.isMobileDrawer
                ? 'fixed left-0 right-0 bottom-[calc(100%+0.5rem)] z-[60] mx-4'
                : 'absolute z-40 left-0 mt-2 transform -translate-x-1/2 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none',
              'bg-white shadow-lg rounded-md ring-1 ring-black ring-opacity-5 focus:outline-none'
            )}
            style={{
              maxHeight: '80vh',
              overflowY: 'auto'
            }}
          >
            {props.isSinglePicker ? (
              <DayPickerSingleDateController
                noBorder={true}
                initialVisibleMonth={() => moment(props.startDate)}
                date={parseDateTime(DateTime.fromISO(props.startDate.toISO()).set({hour: 12})?.toISO(), camp.timezone, { asMoment: true })}
                onDateChange={(date) => {
                  props.setStartDate(DateTime.fromJSDate(date?.toDate()).toUTC(), 0)
                  props.setEndDate(DateTime.fromJSDate(date?.toDate()).toUTC(), 23)
                }}
                isOutsideRange={(day: Moment) => false}
                focused={false}
                onFocusChange={onFocusChange}
                numberOfMonths={1}
                hideKeyboardShortcutsPanel={true}
              />
            ) : (
              <DayPickerRangeController
                startDate={moment.parseZone(props.startDate.toISO())}
                endDate={
                  props.endDate && !props.startDate.hasSame(props.endDate, "day") && moment.parseZone(props.endDate.toISO())
                }
                onDatesChange={onChange}
                focusedInput={focusedInput}
                onFocusChange={onFocusChange}
                initialVisibleMonth={() => moment(props.startDate.toString()) || moment()}
                noBorder={true}
                hideKeyboardShortcutsPanel={true}
              />
            )}
          </Menu.Items>
        </Transition>
      </Menu>
    </div>
  )
}

export default ReportsDatePicker
